import type {
  Catalog,
  Career,
  DisciplineId,
  EntityType,
  LearningResource,
  Sector,
  Skill,
  SkillGroupId,
  Source,
  Technology,
  Trend,
  TrendStatus,
} from "./schema";
import { CatalogSchema, collectReferentialIssues } from "./schema";
import rawCatalog from "./catalog.json";

export type RadarView = EntityType;

export interface ObservatoryConfig {
  includeDraft: boolean;
  catalogUrl?: string;
}

export interface RadarEntity {
  id: string;
  name: string;
  type: RadarView;
  trendStatus: TrendStatus;
  disciplineIds: DisciplineId[];
  geography: string[];
  groupId?: string;
  relatedSkillIds: string[];
  relatedCareerIds: string[];
  relatedTechnologyIds: string[];
  relatedSectorIds: string[];
  relatedResourceIds: string[];
  sourceIds: string[];
  description: string;
  whyItMatters: string;
  aliases: string[];
  lastReviewedAt: string;
  evidenceLevel: string;
  referencePeriod?: string;
}

export interface FilterState {
  discipline: "all" | DisciplineId;
  view: RadarView;
  trend: "all" | TrendStatus;
  geography: "all" | "europe" | "ukraine";
  group: "all" | SkillGroupId;
  q: string;
  entity?: string;
  connections: boolean;
}

export function parseCatalog(data: unknown): Catalog {
  const catalog = CatalogSchema.parse(data);
  const issues = collectReferentialIssues(catalog);
  const errors = issues.filter((i) => i.level === "error");
  if (errors.length) {
    throw new Error(errors.map((e) => e.message).join("\n"));
  }
  return catalog;
}

export function publishedOnly<T extends { status: string }>(items: T[], includeDraft: boolean): T[] {
  if (includeDraft) return items.filter((i) => i.status !== "archived");
  return items.filter((i) => i.status === "published");
}

export class StaticEvidenceProvider {
  readonly catalog: Catalog;
  readonly includeDraft: boolean;

  constructor(catalog: Catalog, includeDraft = false) {
    this.catalog = catalog;
    this.includeDraft = includeDraft;
  }

  skills(): Skill[] {
    return publishedOnly(this.catalog.skills, this.includeDraft);
  }
  careers(): Career[] {
    return publishedOnly(this.catalog.careers, this.includeDraft);
  }
  technologies(): Technology[] {
    return publishedOnly(this.catalog.technologies, this.includeDraft);
  }
  trends(): Trend[] {
    return publishedOnly(this.catalog.trends, this.includeDraft);
  }
  sectors(): Sector[] {
    return publishedOnly(this.catalog.sectors, this.includeDraft);
  }
  resources(): LearningResource[] {
    return publishedOnly(this.catalog.resources, this.includeDraft);
  }
  sources(): Source[] {
    return this.catalog.sources.filter((s) => s.status === "published" || this.includeDraft);
  }

  skillById(id: string): Skill | undefined {
    return this.skills().find((s) => s.id === id);
  }
  careerById(id: string): Career | undefined {
    return this.careers().find((c) => c.id === id);
  }
  sourceById(id: string): Source | undefined {
    return this.catalog.sources.find((s) => s.id === id);
  }
  resourceById(id: string): LearningResource | undefined {
    return this.resources().find((r) => r.id === id);
  }

  skillsById(): Map<string, Skill> {
    return new Map(this.skills().map((s) => [s.id, s]));
  }

  lastReviewedAt(): string {
    return this.catalog.meta.lastReviewedAt;
  }

  radarEntities(view: RadarView): RadarEntity[] {
    if (view === "technology") {
      return this.technologies().map((t) => ({
        id: t.id,
        name: t.name,
        type: "technology",
        trendStatus: t.trendStatus,
        disciplineIds: t.disciplineIds,
        geography: t.geography,
        groupId: t.categoryGroupId,
        relatedSkillIds: t.relatedSkillIds,
        relatedCareerIds: t.relatedCareerIds,
        relatedTechnologyIds: [],
        relatedSectorIds: t.relatedSectorIds,
        relatedResourceIds: t.relatedResourceIds,
        sourceIds: t.sourceIds,
        description: t.shortDefinition,
        whyItMatters: t.whyItMatters,
        aliases: t.aliases,
        lastReviewedAt: t.lastReviewedAt,
        evidenceLevel: t.evidenceLevel,
      }));
    }
    if (view === "skill") {
      return this.skills().map((s) => ({
        id: s.id,
        name: s.name,
        type: "skill",
        trendStatus: s.trendStatus,
        disciplineIds: s.disciplineIds,
        geography: s.geography,
        groupId: s.groupId,
        relatedSkillIds: [],
        relatedCareerIds: s.relatedCareerIds,
        relatedTechnologyIds: s.relatedTechnologyIds,
        relatedSectorIds: [],
        relatedResourceIds: s.relatedResourceIds,
        sourceIds: s.sourceIds,
        description: s.shortDefinition,
        whyItMatters: s.whyItMatters,
        aliases: s.aliases,
        lastReviewedAt: s.lastReviewedAt,
        evidenceLevel: s.evidenceLevel,
      }));
    }
    if (view === "career") {
      return this.careers().map((c) => ({
        id: c.id,
        name: c.title,
        type: "career",
        trendStatus: c.trendStatus,
        disciplineIds: c.disciplineIds,
        geography: c.geography,
        relatedSkillIds: c.requiredSkills.map((r) => r.skillId),
        relatedCareerIds: c.relatedCareerIds,
        relatedTechnologyIds: c.relatedTechnologyIds,
        relatedSectorIds: c.sectorIds,
        relatedResourceIds: [],
        sourceIds: c.sourceIds,
        description: c.description,
        whyItMatters: c.description,
        aliases: c.aliases,
        lastReviewedAt: c.lastReviewedAt,
        evidenceLevel: c.evidenceLevel,
      }));
    }
    return this.sectors().map((s) => ({
      id: s.id,
      name: s.name,
      type: "sector",
      trendStatus: s.trendStatus,
      disciplineIds: s.disciplineIds,
      geography: s.geography,
      relatedSkillIds: [],
      relatedCareerIds: this.careers()
        .filter((c) => c.sectorIds.includes(s.id))
        .map((c) => c.id),
      relatedTechnologyIds: this.technologies()
        .filter((t) => t.relatedSectorIds.includes(s.id))
        .map((t) => t.id),
      relatedSectorIds: [],
      relatedResourceIds: [],
      sourceIds: s.sourceIds,
      description: s.shortDefinition,
      whyItMatters: s.shortDefinition,
      aliases: s.aliases,
      lastReviewedAt: s.lastReviewedAt,
      evidenceLevel: s.evidenceLevel,
    }));
  }
}

export interface TrendDataProvider {
  skills(): Skill[];
  careers(): Career[];
  technologies(): Technology[];
  trends(): Trend[];
  lastReviewedAt(): string;
}

export function bundledCatalog(): Catalog {
  return parseCatalog(rawCatalog);
}

export async function loadProvider(config: ObservatoryConfig): Promise<StaticEvidenceProvider> {
  if (config.catalogUrl) {
    try {
      const response = await fetch(config.catalogUrl);
      if (response.ok) {
        const data: unknown = await response.json();
        return new StaticEvidenceProvider(parseCatalog(data), config.includeDraft);
      }
    } catch {
      /* fall back to bundled catalog */
    }
  }
  return new StaticEvidenceProvider(bundledCatalog(), config.includeDraft);
}
